import { useState, useEffect, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";

// ============================================================================
// brioCAST — Provisioning (場域管理) — P2a/P2b
// ============================================================================
// Business account overview + Venue CRUD + Zone CRUD + self-service pairing QR.
// Consumes the live briocast-api provisioning endpoints.
//
// Nav item:  { id: "provisioning", label: "場域管理", icon: "🏗️", cap: "SCREEN_NETWORK", group: "ops" }
// Route:     case "provisioning": return hasCap(account, "SCREEN_NETWORK") ? <ProvisioningPage account={account} /> : <NoCap cap="SCREEN_NETWORK" />;
// API base:  /api/v1/provisioning
//
// Contract notes (do NOT deviate):
//  - Zone routes are NESTED under venue: /venues/:venue_id/zones[/:zone_id]
//  - Venue field is `type` (NOT venue_type)
//  - Zone IDs are server-assigned ZONE-NNN — never generated client-side
//  - 422 validation · 404 cross-tenant · 409 delete-guard (has devices)
//
// In scope here: P2b self-service pairing-code + QR.
// Deferred here: template/content management.
// ============================================================================

const API = "https://api.briolabs.io";
const PROV = "/api/v1/provisioning";

// Core fetch — same biz_token bearer + envelope-aware error handling for
// provisioning routes, including self-service pairing-code generation.
async function apiCall(base, path, options = {}) {
  const token = localStorage.getItem("biz_token");
  const res = await fetch(`${API}${base}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  // 204 / empty body (e.g. DELETE) → no JSON to parse.
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.error || json.code || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = json.error || json.code;
    err.body = json;
    throw err;
  }
  return json;
}

const api = (path, options) => apiCall(PROV, path, options);

// API envelopes vary across portal endpoints; unwrap defensively (matches
// the `json.data || json` convention used by DeviceOpsPage / VenueCreativesPage).
function unwrap(json) {
  return (json && json.data !== undefined ? json.data : json) || {};
}
function asArray(json, key) {
  const p = unwrap(json);
  if (Array.isArray(p)) return p;
  if (key && Array.isArray(p[key])) return p[key];
  if (Array.isArray(p.items)) return p.items;
  return [];
}

// Format an ISO timestamp for display; null/invalid → em-dash.
function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("zh-TW", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

// ── Dark theme (matches DeviceOpsPage.jsx / FleetDashboard.jsx) ────────────
const t = {
  bg: "#0F1117", card: "#1A1D27", border: "#2A2E3A",
  accent: "#6C5CE7", accentLight: "#A29BFE", accentBg: "rgba(108,92,231,0.12)",
  success: "#00B894", successBg: "rgba(0,184,148,0.12)",
  warning: "#FDCB6E", warningBg: "rgba(253,203,110,0.15)",
  danger: "#E17055", dangerBg: "rgba(225,112,85,0.12)",
  text: "#E8E8ED", textDim: "#8B8FA3", muted: "#8B8FA3", dim: "#5A5E72",
  mono: "'JetBrains Mono', 'SF Mono', monospace",
};

const VENUE_TYPES = [
  { value: "clinic", label: "診所" },
  { value: "restaurant", label: "餐廳" },
  { value: "retail", label: "零售" },
  { value: "temple", label: "廟宇" },
  { value: "parking", label: "停車場" },
  { value: "office", label: "辦公大樓" },
  { value: "gym", label: "健身房" },
  { value: "ev_charging", label: "EV充電站" },
  { value: "general", label: "一般" },
];
const venueTypeLabel = (v) => VENUE_TYPES.find((x) => x.value === v)?.label || v || "—";

const SCENE_TYPES = [
  { value: "general", label: "一般" },
  { value: "restaurant", label: "餐飲" },
  { value: "clinic", label: "診所" },
  { value: "retail", label: "零售" },
  { value: "office", label: "辦公" },
  { value: "gym", label: "健身" },
  { value: "temple", label: "廟宇" },
  { value: "parking", label: "停車場" },
  { value: "ev_charging", label: "EV 充電" },
];
const sceneTypeLabel = (v) => SCENE_TYPES.find((x) => x.value === v)?.label || v || "—";

const VENUE_STATUS = {
  active: { label: "啟用", color: t.success, bg: t.successBg },
  inactive: { label: "停用", color: t.dim, bg: "#6B728022" },
  suspended: { label: "暫停", color: t.warning, bg: t.warningBg },
  archived: { label: "已封存", color: t.dim, bg: "#6B728022" },
};
function StatusPill({ status }) {
  const ns = status || "active";
  const m = VENUE_STATUS[ns] || { label: status, color: t.textDim, bg: "#6B728022" };
  return (
    <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: m.bg, color: m.color }}>
      {m.label}
    </span>
  );
}

// ── Toast (lightweight inline, matches DeviceOpsPage) ──────────────────────
function Toast({ msg, kind, onClose }) {
  useEffect(() => {
    if (!msg) return;
    const tm = setTimeout(onClose, 3800);
    return () => clearTimeout(tm);
  }, [msg, onClose]);
  if (!msg) return null;
  const colors = {
    success: { bg: t.successBg, fg: t.success, border: t.success },
    error: { bg: t.dangerBg, fg: t.danger, border: t.danger },
    info: { bg: t.accentBg, fg: t.accentLight, border: t.accent },
  }[kind] || { bg: t.card, fg: t.text, border: t.border };
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 1000, maxWidth: 420,
      padding: "12px 18px", borderRadius: 8, fontSize: 13, fontWeight: 500, lineHeight: 1.5,
      background: colors.bg, color: colors.fg, border: `1px solid ${colors.border}`,
      boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    }}>{msg}</div>
  );
}

// Map an API error to a user-facing zh-TW message + toast kind.
function describeError(e, { entity = "資料" } = {}) {
  if (e.status === 401) return { kind: "error", msg: "登入已過期，請重新登入。", auth: true };
  if (e.status === 403) return { kind: "error", msg: "權限不足，無法執行此操作。" };
  if (e.status === 404) return { kind: "error", msg: `找不到該${entity}（可能屬於其他帳戶）。` };
  if (e.status === 422) return { kind: "error", msg: e.message || "欄位驗證失敗，請檢查必填欄位。" };
  return { kind: "error", msg: `操作失敗，請稍後重試。（${e.message || "網路或伺服器錯誤"}）` };
}

// ============================================================================
// Main
// ============================================================================
export default function ProvisioningPage({ account }) {
  const [biz, setBiz] = useState(null);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // "auth" | "load" | null

  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [editingVenue, setEditingVenue] = useState(null); // venue object | "new" | null
  const [toast, setToast] = useState(null);

  const reportError = useCallback((e, opts) => {
    const d = describeError(e, opts);
    if (d.auth) setError("auth");
    setToast({ kind: d.kind, msg: d.msg });
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Business account is best-effort; venue list is the primary payload.
      const [bizJson, venuesJson] = await Promise.allSettled([
        api("/business-account"),
        api("/venues"),
      ]);
      if (bizJson.status === "fulfilled") setBiz(unwrap(bizJson.value));
      if (venuesJson.status === "fulfilled") {
        setVenues(asArray(venuesJson.value, "venues"));
      } else {
        throw venuesJson.reason;
      }
    } catch (e) {
      if (e.status === 401) setError("auth");
      else setError("load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const selectedVenue = venues.find((v) => v.id === selectedVenueId) || null;

  // ── Venue create / edit ──────────────────────────────────────────────
  const saveVenue = async (form, id) => {
    try {
      if (id) {
        const json = await api(`/venues/${id}`, { method: "PATCH", body: form });
        const updated = unwrap(json);
        setVenues((prev) => prev.map((v) => (v.id === id ? { ...v, ...updated } : v)));
        setToast({ kind: "success", msg: "場域已更新" });
      } else {
        const json = await api("/venues", { method: "POST", body: form });
        const created = unwrap(json);
        setVenues((prev) => [...prev, created]);
        setToast({ kind: "success", msg: "場域已建立" });
        if (created.id) setSelectedVenueId(created.id);
      }
      setEditingVenue(null);
      return { ok: true };
    } catch (e) {
      reportError(e, { entity: "場域" });
      return { ok: false, msg: describeError(e, { entity: "場域" }).msg };
    }
  };

  const deleteVenue = async (venue) => {
    try {
      await api(`/venues/${venue.id}`, { method: "DELETE" });
      setVenues((prev) => prev.filter((v) => v.id !== venue.id));
      if (selectedVenueId === venue.id) setSelectedVenueId(null);
      setToast({ kind: "success", msg: "場域已停用" });
    } catch (e) {
      if (e.status === 409) {
        const n = e.body?.device_count;
        setToast({
          kind: "error",
          msg: `此場域仍有裝置綁定，無法刪除。請先 retire 或 transfer 裝置。${n != null ? `（${n} 台）` : ""}`,
        });
      } else {
        reportError(e, { entity: "場域" });
      }
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (error === "auth") {
    return (
      <PageShell>
        <div style={{ color: t.danger, fontSize: 14, padding: 24 }}>
          登入已過期，請重新登入後再試。
          <button onClick={() => { localStorage.removeItem("biz_token"); window.location.reload(); }} style={btnLink}>登出</button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>場域管理 · Provisioning</h2>
        <button onClick={loadAll} style={btnSecondary} disabled={loading}>{loading ? "載入中…" : "↻ 重新整理"}</button>
      </div>

      <AccountOverview biz={biz} account={account} venueCount={venues.length} />

      {error === "load" ? (
        <div style={{ padding: 40, color: t.danger, textAlign: "center" }}>
          暫時無法取得場域資料，稍後請重試。
          <div><button onClick={loadAll} style={btnLink}>重試</button></div>
        </div>
      ) : loading ? (
        <div style={{ padding: 40, color: t.textDim, textAlign: "center" }}>載入中…</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: selectedVenue ? "1fr 1fr" : "1fr", gap: 16, alignItems: "start" }}>
          <VenueList
            venues={venues}
            selectedVenueId={selectedVenueId}
            onSelect={(id) => setSelectedVenueId(id === selectedVenueId ? null : id)}
            onCreate={() => { setEditingVenue("new"); }}
            onEdit={(v) => setEditingVenue(v)}
            onDelete={deleteVenue}
          />
          {selectedVenue && (
            <VenueDetail
              key={selectedVenue.id}
              venue={selectedVenue}
              onEdit={() => setEditingVenue(selectedVenue)}
              onDelete={() => deleteVenue(selectedVenue)}
              onClose={() => setSelectedVenueId(null)}
              onToast={setToast}
              reportError={reportError}
            />
          )}
        </div>
      )}

      {editingVenue && (
        <VenueFormModal
          venue={editingVenue === "new" ? null : editingVenue}
          onClose={() => setEditingVenue(null)}
          onSave={saveVenue}
        />
      )}

      <Toast msg={toast?.msg} kind={toast?.kind} onClose={() => setToast(null)} />
    </PageShell>
  );
}

// ============================================================================
// Business account overview
// ============================================================================
function AccountOverview({ biz, account, venueCount }) {
  const displayName = biz?.display_name || account?.display_name || "—";
  const accountId = biz?.account_id || account?.account_id || "—";
  const tier = biz?.subscription_tier;
  const status = biz?.status;
  return (
    <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
        <KV label="商業帳戶" val={displayName} />
        <KV label="Account ID" val={accountId} mono />
        <KV label="訂閱方案" val={tier ? String(tier) : "—"} />
        <KV label="帳戶狀態" val={status ? <StatusPill status={status} /> : "—"} />
      </div>
      <div style={{ marginTop: 14, fontSize: 12, color: t.dim }}>
        共 {venueCount} 個場域
      </div>
    </div>
  );
}

// ============================================================================
// Venue list
// ============================================================================
function VenueList({ venues, selectedVenueId, onSelect, onCreate, onEdit, onDelete }) {
  return (
    <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: `1px solid ${t.border}` }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>場域列表</div>
        <button onClick={onCreate} style={btnPrimary}>＋ 建立場域</button>
      </div>

      {venues.length === 0 ? (
        <div style={{ padding: "48px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏗️</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: t.text, marginBottom: 6 }}>尚未建立場域</div>
          <div style={{ fontSize: 13, color: t.textDim, marginBottom: 20 }}>建立第一個場域以開始綁定設備與管理區域。</div>
          <button onClick={onCreate} style={btnPrimary}>＋ 建立場域</button>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                {["名稱", "ID", "類型", "狀態", "地點", ""].map((h, i) => (
                  <th key={i} style={{ textAlign: "left", padding: "10px 14px", color: t.textDim, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {venues.map((v) => {
                const selected = v.id === selectedVenueId;
                const location = [v.city, v.district, v.address].filter(Boolean).join(" ");
                return (
                  <tr
                    key={v.id}
                    onClick={() => onSelect(v.id)}
                    style={{
                      borderBottom: `1px solid ${t.border}33`, cursor: "pointer",
                      background: selected ? t.accentBg : "transparent",
                    }}
                  >
                    <td style={{ padding: "12px 14px", fontWeight: 600 }}>
                      {v.name || "—"}
                      {v.name_zh && v.name_zh !== v.name && (
                        <div style={{ fontSize: 11, color: t.dim, fontWeight: 400 }}>{v.name_zh}</div>
                      )}
                    </td>
                    <td style={{ padding: "12px 14px", fontFamily: t.mono, fontSize: 11, color: t.textDim }}>{v.id}</td>
                    <td style={{ padding: "12px 14px", color: t.textDim }}>{venueTypeLabel(v.type)}</td>
                    <td style={{ padding: "12px 14px" }}><StatusPill status={v.status} /></td>
                    <td style={{ padding: "12px 14px", color: t.textDim, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {location || "—"}
                      {(v.zone_count != null || v.device_count != null) && (
                        <div style={{ fontSize: 11, color: t.dim }}>
                          {v.zone_count != null && `${v.zone_count} 區域`}
                          {v.zone_count != null && v.device_count != null && " · "}
                          {v.device_count != null && `${v.device_count} 台`}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "12px 14px", textAlign: "right", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => onEdit(v)} style={btnLinkSm}>編輯</button>
                      <button onClick={() => onDelete(v)} style={{ ...btnLinkSm, color: t.danger }}>停用</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Venue detail + zones
// ============================================================================
function VenueDetail({ venue, onEdit, onDelete, onClose, onToast, reportError }) {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoneError, setZoneError] = useState(false);
  const [editingZone, setEditingZone] = useState(null); // zone | "new" | null
  const [pairing, setPairing] = useState(null); // { zoneId: string|null } when open

  const loadZones = useCallback(async () => {
    setLoading(true);
    setZoneError(false);
    try {
      const json = await api(`/venues/${venue.id}/zones`);
      const list = asArray(json, "zones");
      // Sort by sort_order then id (contract requirement).
      list.sort((a, b) => {
        const sa = a.sort_order ?? Number.MAX_SAFE_INTEGER;
        const sb = b.sort_order ?? Number.MAX_SAFE_INTEGER;
        if (sa !== sb) return sa - sb;
        return String(a.id || "").localeCompare(String(b.id || ""));
      });
      setZones(list);
    } catch (e) {
      setZoneError(true);
      reportError(e, { entity: "區域" });
    } finally {
      setLoading(false);
    }
  }, [venue.id, reportError]);

  useEffect(() => { loadZones(); }, [loadZones]);

  const saveZone = async (form, zoneId) => {
    try {
      if (zoneId) {
        const json = await api(`/venues/${venue.id}/zones/${zoneId}`, { method: "PATCH", body: form });
        const updated = unwrap(json);
        setZones((prev) => prev.map((z) => (z.id === zoneId ? { ...z, ...updated } : z)));
        onToast({ kind: "success", msg: "區域已更新" });
      } else {
        // Backend assigns ZONE-NNN — never send an id.
        const json = await api(`/venues/${venue.id}/zones`, { method: "POST", body: form });
        const created = unwrap(json);
        setZones((prev) => [...prev, created]);
        onToast({ kind: "success", msg: "區域已建立" });
      }
      setEditingZone(null);
      loadZones();
      return { ok: true };
    } catch (e) {
      reportError(e, { entity: "區域" });
      return { ok: false, msg: describeError(e, { entity: "區域" }).msg };
    }
  };

  const deleteZone = async (zone) => {
    if (!window.confirm(`確定要停用區域「${zone.name || zone.id}」？`)) return;
    try {
      await api(`/venues/${venue.id}/zones/${zone.id}`, { method: "DELETE" });
      setZones((prev) => prev.filter((z) => z.id !== zone.id));
      onToast({ kind: "success", msg: "區域已停用" });
    } catch (e) {
      if (e.status === 409) {
        const n = e.body?.device_count;
        onToast({
          kind: "error",
          msg: `此區域仍有裝置綁定，無法刪除。請先 retire 或 transfer 裝置。${n != null ? `（${n} 台）` : ""}`,
        });
      } else {
        reportError(e, { entity: "區域" });
      }
    }
  };

  const location = [venue.city, venue.district, venue.address].filter(Boolean).join(" ");

  return (
    <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "14px 16px", borderBottom: `1px solid ${t.border}` }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{venue.name || "—"}</div>
          <div style={{ fontSize: 11, color: t.dim, fontFamily: t.mono, marginTop: 2 }}>{venue.id}</div>
        </div>
        <button onClick={onClose} style={btnLinkSm}>✕ 關閉</button>
      </div>

      <div style={{ padding: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginBottom: 8 }}>
          {venue.name_zh && <KV label="中文名稱" val={venue.name_zh} />}
          <KV label="類型" val={venueTypeLabel(venue.type)} />
          <KV label="預設場景" val={venue.default_scene_type ? sceneTypeLabel(venue.default_scene_type) : "未設定"} />
          <KV label="狀態" val={<StatusPill status={venue.status} />} />
          {venue.contact_phone && <KV label="聯絡電話" val={venue.contact_phone} />}
          {location && <KV label="地點" val={location} />}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <button onClick={onEdit} style={btnSecondary}>編輯場域</button>
          <button onClick={() => setPairing({ zoneId: null })} style={btnPrimary}>📺 產生配對碼</button>
          <button onClick={onDelete} style={btnDangerOutline}>停用場域</button>
        </div>
      </div>

      {/* Zones */}
      <div style={{ borderTop: `1px solid ${t.border}`, padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>區域 ({zones.length})</div>
          <button onClick={() => setEditingZone("new")} style={btnPrimarySm}>＋ 新增區域</button>
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: t.textDim, fontSize: 13 }}>載入中…</div>
        ) : zoneError ? (
          <div style={{ padding: 16, textAlign: "center", color: t.danger, fontSize: 13 }}>
            無法載入區域。<button onClick={loadZones} style={btnLink}>重試</button>
          </div>
        ) : zones.length === 0 ? (
          <div style={{ padding: 16, textAlign: "center", color: t.textDim, fontSize: 13 }}>尚未建立區域</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                {["ID", "名稱", "場景", "說明", "排序", ""].map((h, i) => (
                  <th key={i} style={{ textAlign: "left", padding: "8px 10px", color: t.textDim, fontSize: 11, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => (
                <tr key={z.id} style={{ borderBottom: `1px solid ${t.border}33` }}>
                  <td style={{ padding: "10px", fontFamily: t.mono, fontSize: 11, color: t.accentLight }}>{z.id}</td>
                  <td style={{ padding: "10px", fontWeight: 600 }}>{z.name || "—"}</td>
                  <td style={{ padding: "10px", color: z.scene_type ? t.text : t.textDim }}>
                    {z.scene_type ? sceneTypeLabel(z.scene_type) : "繼承場域"}
                    {!z.scene_type && venue.default_scene_type && (
                      <div style={{ fontSize: 10, color: t.dim, marginTop: 2 }}>
                        {sceneTypeLabel(venue.default_scene_type)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "10px", color: t.textDim, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{z.description || "—"}</td>
                  <td style={{ padding: "10px", color: t.textDim }}>{z.sort_order ?? "—"}</td>
                  <td style={{ padding: "10px", textAlign: "right", whiteSpace: "nowrap" }}>
                    <button onClick={() => setPairing({ zoneId: z.id })} style={btnLinkSm}>配對碼</button>
                    <button onClick={() => setEditingZone(z)} style={btnLinkSm}>編輯</button>
                    <button onClick={() => deleteZone(z)} style={{ ...btnLinkSm, color: t.danger }}>停用</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editingZone && (
        <ZoneFormModal
          zone={editingZone === "new" ? null : editingZone}
          onClose={() => setEditingZone(null)}
          onSave={saveZone}
        />
      )}

      {pairing && (
        <PairingCodeModal
          venue={venue}
          zones={zones}
          initialZoneId={pairing.zoneId}
          onClose={() => setPairing(null)}
          onToast={onToast}
          reportError={reportError}
        />
      )}
    </div>
  );
}

// ============================================================================
// Pairing-code generation + QR (P2b)
// ============================================================================
// Creates a zone-aware v2 pairing code via the LIVE provisioning endpoint and
// shows it for the on-site TV installer. The raw code is only available from
// the creation response — never re-fetchable — so it is shown once here and
// not persisted.
export function PairingCodeModal({ venue, zones, initialZoneId, onClose, onToast, reportError }) {
  const [zoneId, setZoneId] = useState(initialZoneId || ""); // "" = venue-wide
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // the created code object
  const [errMsg, setErrMsg] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedQr, setCopiedQr] = useState(false);

  const generate = useCallback(async () => {
    setBusy(true);
    setErrMsg(null);
    try {
      const body = { venue_id: venue.id, count: 1 };
      if (zoneId) body.zone_id = zoneId;
      const json = await api("/pairing-codes", { method: "POST", body });
      const data = unwrap(json);
      const nested = Array.isArray(data.codes) ? data.codes[0] : null;
      const rawCode = data.pairing_code || nested?.code;
      if (!rawCode) throw new Error("回應缺少配對碼");

      const qrPayload = data.qr_payload || `briocast://pair?code=${encodeURIComponent(rawCode)}`;
      setResult({
        ...(nested || {}),
        id: data.pairing_code_id || nested?.id || null,
        code: rawCode,
        venue_id: data.venue_id || nested?.venue_id || venue.id,
        zone_id: data.zone_id ?? nested?.zone_id ?? null,
        expires_at: data.expires_at || data.preprovision_expires_at || nested?.preprovision_expires_at || null,
        preprovision_expires_at: data.preprovision_expires_at || nested?.preprovision_expires_at || data.expires_at || null,
        activation_expires_at: data.activation_expires_at || nested?.activation_expires_at || null,
        qr_payload: qrPayload,
      });
    } catch (e) {
      // 422 ZONE_NOT_IN_VENUE is the one domain-specific case to spell out.
      if (e.status === 422 && (e.code === "ZONE_NOT_IN_VENUE" || /ZONE_NOT_IN_VENUE/i.test(e.message || ""))) {
        setErrMsg("此區域不屬於該場域，請重新選擇");
      } else if (e.status === 401) {
        reportError(e); // routes to global auth/session handling
        onClose();
        return;
      } else {
        setErrMsg(`產生失敗，請稍後重試。（${e.message || "網路或伺服器錯誤"}）`);
      }
    } finally {
      setBusy(false);
    }
  }, [venue.id, zoneId, reportError, onClose]);

  const copyCode = async () => {
    if (!result?.code) return;
    try {
      await navigator.clipboard.writeText(result.code);
      setCopied(true);
      onToast({ kind: "success", msg: "已複製配對碼" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onToast({ kind: "error", msg: "無法複製，請手動選取代碼" });
    }
  };

  const copyQrPayload = async () => {
    const payload = result?.qr_payload || result?.code;
    if (!payload) return;
    try {
      await navigator.clipboard.writeText(payload);
      setCopiedQr(true);
      onToast({ kind: "success", msg: "已複製 QR payload" });
      setTimeout(() => setCopiedQr(false), 2000);
    } catch {
      onToast({ kind: "error", msg: "無法複製，請手動選取 QR payload" });
    }
  };

  const zoneLabel = (id) => {
    const z = zones.find((x) => x.id === id);
    return z ? `${z.id}${z.name ? ` · ${z.name}` : ""}` : id;
  };

  return (
    <ModalShell title="產生配對碼" onClose={onClose}>
      {!result ? (
        <>
          <div style={{ fontSize: 12, color: t.textDim, marginBottom: 14, lineHeight: 1.6 }}>
            為 <strong style={{ color: t.text }}>{venue.name || venue.id}</strong> 產生一組電視配對碼。
            可綁定整個場域或指定區域。
          </div>
          <Field label="綁定範圍">
            <select value={zoneId} onChange={(e) => setZoneId(e.target.value)} style={inputStyle} disabled={busy}>
              <option value="">整個場域 · venue-wide</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>{z.id}{z.name ? ` · ${z.name}` : ""}</option>
              ))}
            </select>
          </Field>
          {errMsg && <div style={{ color: t.danger, fontSize: 12, marginBottom: 12 }}>{errMsg}</div>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
            <button onClick={onClose} style={btnSecondary} disabled={busy}>取消</button>
            <button onClick={generate} style={btnPrimary} disabled={busy}>{busy ? "產生中…" : "產生配對碼"}</button>
          </div>
        </>
      ) : (
        <PairingCodeResult
          code={result}
          venue={venue}
          zoneLabel={zoneLabel}
          copied={copied}
          copiedQr={copiedQr}
          onCopy={copyCode}
          onCopyQr={copyQrPayload}
          onClose={onClose}
        />
      )}
    </ModalShell>
  );
}

function PairingCodeResult({ code, venue, zoneLabel, copied, copiedQr, onCopy, onCopyQr, onClose }) {
  const scope = code.zone_id ? zoneLabel(code.zone_id) : "整個場域 · venue-wide";
  const qrPayload = code.qr_payload || code.code;
  return (
    <div>
      {/* Code — large + monospaced */}
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <div style={{
          fontFamily: t.mono, fontSize: 34, fontWeight: 700, letterSpacing: 4,
          color: t.text, padding: "12px 0", userSelect: "all",
        }}>{code.code}</div>
        <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
          <button onClick={onCopy} style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12 }}>
            {copied ? "✓ 已複製" : "📋 複製代碼"}
          </button>
          <button onClick={onCopyQr} style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12 }}>
            {copiedQr ? "✓ 已複製 QR" : "📋 複製 QR payload"}
          </button>
        </div>
      </div>

      {/* QR — encodes the raw code string only (never the token/secrets) */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
        <div style={{ background: "#fff", padding: 12, borderRadius: 10 }}>
          <QRCodeCanvas value={qrPayload} size={180} marginSize={2} level="M" />
        </div>
      </div>

      {/* Scope + expiry */}
      <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 8, padding: 14, marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
          <KV label="場域" val={venue.name || venue.id} />
          <KV label="綁定範圍" val={scope} />
          <KV label="預備到期" val={fmtTime(code.preprovision_expires_at)} />
          <KV label="啟用到期" val={fmtTime(code.activation_expires_at)} />
        </div>
      </div>

      {/* Installer instructions */}
      <div style={{ fontSize: 12, color: t.textDim, lineHeight: 1.7, marginBottom: 16 }}>
        在電視上開啟 brioCAST → 輸入此配對碼（8 碼）或掃描 QR。
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={onClose} style={btnPrimary}>完成</button>
      </div>
    </div>
  );
}

// ============================================================================
// Venue create / edit modal
// ============================================================================
function VenueFormModal({ venue, onClose, onSave }) {
  const editing = !!venue;
  const [form, setForm] = useState({
    name: venue?.name || "",
    name_zh: venue?.name_zh || "",
    type: venue?.type || "general",
    address: venue?.address || "",
    city: venue?.city || "",
    district: venue?.district || "",
    contact_phone: venue?.contact_phone || "",
    status: venue?.status || "active",
    default_scene_type: venue?.default_scene_type || "",
  });
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState(null);
  const [msg, setMsg] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setFieldError(null);
    setMsg(null);
    if (!form.name.trim()) { setFieldError("name"); setMsg("名稱為必填"); return; }
    setBusy(true);
    // On edit, send only the editable fields (partial PATCH is fine).
    const r = await onSave({
      ...form,
      name: form.name.trim(),
      default_scene_type: form.default_scene_type || null,
    }, editing ? venue.id : null);
    setBusy(false);
    if (!r.ok && r.msg) setMsg(r.msg);
  };

  return (
    <ModalShell title={editing ? "編輯場域" : "建立場域"} onClose={onClose}>
      <Field label="名稱 *" error={fieldError === "name"}>
        <input value={form.name} onChange={set("name")} placeholder="例：大明診所" style={inputStyle} autoFocus />
      </Field>
      <Field label="中文名稱">
        <input value={form.name_zh} onChange={set("name_zh")} placeholder="（選填）" style={inputStyle} />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="類型">
          <select value={form.type} onChange={set("type")} style={inputStyle}>
            {VENUE_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="狀態">
          <select value={form.status} onChange={set("status")} style={inputStyle}>
            <option value="active">啟用</option>
            <option value="inactive">停用</option>
            <option value="suspended">暫停</option>
          </select>
        </Field>
      </div>
      <Field label="預設場景">
        <select value={form.default_scene_type} onChange={set("default_scene_type")} style={inputStyle}>
          <option value="">繼承系統預設 / 未設定</option>
          {SCENE_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div style={{ fontSize: 11, color: t.dim, marginTop: 6 }}>
          場域預設會作為區域未設定時的 fallback。
        </div>
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="城市"><input value={form.city} onChange={set("city")} placeholder="（選填）" style={inputStyle} /></Field>
        <Field label="行政區"><input value={form.district} onChange={set("district")} placeholder="（選填）" style={inputStyle} /></Field>
      </div>
      <Field label="地址"><input value={form.address} onChange={set("address")} placeholder="（選填）" style={inputStyle} /></Field>
      <Field label="聯絡電話"><input value={form.contact_phone} onChange={set("contact_phone")} placeholder="（選填）" style={inputStyle} /></Field>

      {msg && <div style={{ color: t.danger, fontSize: 12, marginBottom: 12 }}>{msg}</div>}
      <ModalActions onClose={onClose} onSubmit={submit} busy={busy} submitLabel={editing ? "儲存" : "建立"} />
    </ModalShell>
  );
}

// ============================================================================
// Zone create / edit modal
// ============================================================================
function ZoneFormModal({ zone, onClose, onSave }) {
  const editing = !!zone;
  const [form, setForm] = useState({
    name: zone?.name || "",
    description: zone?.description || "",
    sort_order: zone?.sort_order ?? "",
    scene_type: zone?.scene_type || "",
  });
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState(null);
  const [msg, setMsg] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setFieldError(null);
    setMsg(null);
    if (!form.name.trim()) { setFieldError("name"); setMsg("名稱為必填"); return; }
    const body = {
      name: form.name.trim(),
      description: form.description,
      scene_type: form.scene_type || null,
    };
    // sort_order is optional; only send a number when provided.
    if (form.sort_order !== "" && form.sort_order != null) {
      const n = parseInt(form.sort_order, 10);
      if (!Number.isNaN(n)) body.sort_order = n;
    }
    setBusy(true);
    const r = await onSave(body, editing ? zone.id : null);
    setBusy(false);
    if (!r.ok && r.msg) setMsg(r.msg);
  };

  return (
    <ModalShell title={editing ? "編輯區域" : "新增區域"} onClose={onClose}>
      {editing && (
        <Field label="Zone ID（系統指派）">
          <input value={zone.id} readOnly style={{ ...inputStyle, color: t.dim, cursor: "not-allowed" }} />
        </Field>
      )}
      <Field label="名稱 *" error={fieldError === "name"}>
        <input value={form.name} onChange={set("name")} placeholder="例：一樓候診區" style={inputStyle} autoFocus />
      </Field>
      <Field label="說明">
        <input value={form.description} onChange={set("description")} placeholder="（選填）" style={inputStyle} />
      </Field>
      <Field label="區域場景">
        <select value={form.scene_type} onChange={set("scene_type")} style={inputStyle}>
          <option value="">繼承場域預設</option>
          {SCENE_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div style={{ fontSize: 11, color: t.dim, marginTop: 6 }}>
          區域場景會優先於場域預設。
        </div>
      </Field>
      <Field label="排序">
        <input value={form.sort_order} onChange={set("sort_order")} type="number" placeholder="（選填，數字越小越前）" style={inputStyle} />
      </Field>

      {msg && <div style={{ color: t.danger, fontSize: 12, marginBottom: 12 }}>{msg}</div>}
      <ModalActions onClose={onClose} onSubmit={submit} busy={busy} submitLabel={editing ? "儲存" : "建立"} />
    </ModalShell>
  );
}

// ============================================================================
// Shared bits
// ============================================================================
function PageShell({ children }) {
  return (
    <div style={{ color: t.text, fontFamily: "DM Sans, Noto Sans TC, sans-serif" }}>
      {children}
    </div>
  );
}

function KV({ label, val, mono }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: t.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: t.text, fontFamily: mono ? t.mono : undefined, wordBreak: "break-all" }}>{val}</div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 11, color: error ? t.danger : t.textDim, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</label>
      {children}
    </div>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 24, minWidth: 420, maxWidth: 520, width: "100%", maxHeight: "90vh", overflowY: "auto", color: t.text }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ onClose, onSubmit, busy, submitLabel }) {
  return (
    <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
      <button onClick={onClose} style={btnSecondary} disabled={busy}>取消</button>
      <button onClick={onSubmit} style={btnPrimary} disabled={busy}>{busy ? "處理中…" : submitLabel}</button>
    </div>
  );
}

// ── Styles (match DeviceOpsPage button language) ───────────────────────────
const btnBase = { padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", border: "1px solid transparent", transition: "opacity 0.15s" };
const btnPrimary = { ...btnBase, background: t.accent, color: "#fff", borderColor: t.accent };
const btnPrimarySm = { ...btnPrimary, padding: "6px 12px", fontSize: 12 };
const btnSecondary = { ...btnBase, background: "transparent", color: t.text, borderColor: t.border };
const btnDangerOutline = { ...btnBase, background: "transparent", color: t.danger, borderColor: t.danger };
const btnLink = { background: "transparent", border: "none", color: t.accentLight, cursor: "pointer", fontSize: 13, padding: "4px 8px" };
const btnLinkSm = { background: "transparent", border: "none", color: t.accentLight, cursor: "pointer", fontSize: 12, padding: "2px 6px", fontWeight: 600 };
const inputStyle = { width: "100%", padding: "10px 12px", background: t.bg, border: `1px solid ${t.border}`, borderRadius: 8, color: t.text, fontSize: 13, outline: "none", boxSizing: "border-box" };
